import { Component, effect, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ShoppingCartStore } from '../../../services/api/shopping-cart/shopping-cart-store.service';
import { ShoppingCartFactoryService } from '../../../factory/shoppingCart/shopping-cart-factory.service';
import { IShoppingCartForm } from '../../../entities/shopping-cart-entity';
import { SectionComponent } from '../../../components/layout/section/section.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { LanguageSwitcherComponent } from '../../../components/data-input/form-inputs/language-switcher/language-switcher.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { TranslationTextInputComponent } from '../../../components/data-input/form-inputs/translation-text-input/translation-text-input.component';
import { ToggleComponent } from '../../../components/data-input/form-inputs/toggle/toggle.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { EEntityType, ERelationType, INormalText } from '@web/dtos';
import { ElementFocusService } from '../../../services/support/element-focus-support/element-focus-support.service';
import { Subscription } from 'rxjs';
import { FormTracking } from '../form-tracking/form-tracking.component';
import { map, Observable } from 'rxjs';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';

interface IShoppingCartTextForm {
  shoppingCartPriceNetLabel: FormControl<INormalText[]>;
  shoppingCartVatLabel: FormControl<INormalText[]>;
}

@Component({
  selector: 'aw-shopping-cart-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SectionComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    SectionHeaderActionsComponent,
    ButtonComponent,
    LanguageSwitcherComponent,
    LoadingAnimationComponent,
    TranslationTextInputComponent,
    ToggleComponent,
  ],
  templateUrl: './shopping-cart-page.component.html',
  styles: ``,
})
export class ShoppingCartPageComponent
  extends FormTracking<IShoppingCartForm>
  implements OnDestroy
{
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly elementFocusService: ElementFocusService =
    inject(ElementFocusService);
  protected readonly shoppingCartStore: ShoppingCartStore =
    inject(ShoppingCartStore);
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  private readonly shoppingCartFactory: ShoppingCartFactoryService = inject(
    ShoppingCartFactoryService
  );
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  private readonly filterTexts: FilterTextsPipe = inject(FilterTextsPipe);

  public shoppingCartForm: FormGroup<IShoppingCartForm> =
    this.shoppingCartFactory.buildForm(this.shoppingCartStore.shoppingCart());
  public languageForm: FormGroup = new FormGroup({
    language: new FormControl('DE', [Validators.required]),
  });
  public shoppingCartTextForm: FormGroup<IShoppingCartTextForm> =
    this.formBuilder.group<IShoppingCartTextForm>({
      shoppingCartPriceNetLabel: this.formBuilder.nonNullable.control<
        INormalText[]
      >([], [Validators.required]),
      shoppingCartVatLabel: this.formBuilder.nonNullable.control<INormalText[]>(
        [],
        [Validators.required]
      ),
    });

  protected readonly EntityType: typeof EEntityType = EEntityType;
  protected readonly TextRelationType: typeof ERelationType = ERelationType;

  private readonly subscription: Subscription = new Subscription();
  private initialTextFormValue!: FormGroup<IShoppingCartTextForm>['value'];

  constructor() {
    super();
    effect((): void => {
      this.shoppingCartForm = this.shoppingCartFactory.buildForm(
        this.deepCopyService.createDeepCopy(
          this.shoppingCartStore.shoppingCart()
        )
      );
      this.createActivitySubscription();
      this.shoppingCartForm.controls.showVat.updateValueAndValidity();
      this.initFormTracking(this.shoppingCartForm);
    });
    effect(() => {
      const texts: INormalText[] = this.textStore.texts();
      const freshTexts: INormalText[] =
        this.deepCopyService.createDeepCopy(texts);
      this.shoppingCartTextForm.controls.shoppingCartPriceNetLabel.setValue(
        this.filterTexts.transform(
          freshTexts,
          ERelationType.shoppingCartPriceNetLabel,
          EEntityType.shoppingCart
        )
      );
      this.shoppingCartTextForm.controls.shoppingCartVatLabel.setValue(
        this.filterTexts.transform(
          freshTexts,
          ERelationType.shoppingCartVatLabel,
          EEntityType.shoppingCart
        )
      );
      this.initialTextFormValue = this.deepCopyService.createDeepCopy(
        this.shoppingCartTextForm.getRawValue()
      );
    });
    effect(() => {
      this.elementFocusService.focusElement(
        !this.shoppingCartStore.isLoading() &&
          !!this.shoppingCartStore.shoppingCart()
      );
    });
    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      if (
        this.hasUnsavedChanges(this.shoppingCartForm) ||
        this.hasTextFormChanges()
      ) {
        event.preventDefault();
      }
    };
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public override hasUnsavedChanges(
    form: FormGroup<IShoppingCartForm>
  ): boolean {
    return (
      JSON.stringify(this.initialFormValue) !==
        JSON.stringify(form.getRawValue()) || this.hasTextFormChanges()
    );
  }

  public hasTextFormChanges(): boolean {
    return (
      JSON.stringify(this.shoppingCartTextForm.getRawValue()) !==
      JSON.stringify(this.initialTextFormValue)
    );
  }

  save(): boolean {
    if (!this.shoppingCartForm.valid) {
      this.shoppingCartForm.markAllAsTouched();
      return false;
    }
    this.shoppingCartStore.save(
      this.shoppingCartFactory.buildEntity(this.shoppingCartForm!)
    );
    if (
      this.shoppingCartTextForm.value.shoppingCartVatLabel &&
      this.shoppingCartTextForm.value.shoppingCartPriceNetLabel
    ) {
      this.textStore.save(
        this.shoppingCartTextForm.value.shoppingCartVatLabel.concat(
          this.shoppingCartTextForm.value.shoppingCartPriceNetLabel
        ) as INormalText[]
      );
    }
    return true;
  }

  resetFormChanges(): void {
    this.shoppingCartStore.reload();
    this.textStore.reload();
  }

  handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<{
      icon: string;
      iconBackgroundColor: string;
    }>(ResetChangesDialogComponent, {
      headline: this.cmsTextProvider.shared.revertChanges,
      width: '600px',
      icon: 'warning',
      iconBackgroundColor: '#eed202',
    });
    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        if (result.acceptChanges) {
          this.save();
        } else {
          this.resetFormChanges();
        }
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }

  createActivitySubscription(): void {
    this.subscription.add(
      this.shoppingCartForm.controls.showVat.valueChanges.subscribe(
        (isActive: boolean) => {
          if (isActive) {
            this.shoppingCartTextForm.controls.shoppingCartPriceNetLabel.enable();
            this.shoppingCartTextForm.controls.shoppingCartVatLabel.enable();
          } else {
            this.shoppingCartTextForm.controls.shoppingCartPriceNetLabel.disable();
            this.shoppingCartTextForm.controls.shoppingCartVatLabel.disable();
          }
        }
      )
    );
  }
}
